import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Loader2, ChevronRight, ChevronLeft, Edit3, Trash2, Calendar, Eye, Search, Filter } from 'lucide-react';
import { useNews } from '../../../hooks/useNews';
import { useNewsCategories } from '../../../hooks/useNewsCategories';
import { useNewsTags } from '../../../hooks/useNewsTags';
import { useNewsImages } from '../../../hooks/useNewsImages';
import { ImageBrowser } from '../../../components/media/ImageBrowser';
import { useAdminNotifications } from '../../../hooks/useAdminNotifications';
import { NewsArticleCard } from '../../../components/news/NewsArticleCard';
import { NewsArticleForm } from '../../../components/news/NewsArticleForm';
import type { NewsArticleWithRelations, NewsTag, NewsCategory } from '../../../types/news';
import Toast from '../../../components/shared/Toast';
import { useAuth } from '../../../hooks/useAuth';
import { Pagination } from '../../../components/shared/Pagination';

interface NewsSectionProps {
  activeSection: string;
}

export const NewsSection: React.FC<NewsSectionProps> = ({ activeSection }) => {
  const [articles, setArticles] = useState<NewsArticleWithRelations[]>([]);
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [tags, setTags] = useState<NewsTag[]>([]);
  const [editingArticle, setEditingArticle] = useState<Partial<NewsArticleWithRelations> | null>(null);
  const [selectedTags, setSelectedTags] = useState<NewsTag[]>([]);
  const [showToast, setShowToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showImageBrowser, setShowImageBrowser] = useState(false);
  const articlesPerPage = 6;

  const { 
    loading: articlesLoading,
    fetchArticles,
    createArticle,
    updateArticle,
    deleteArticle
  } = useNews();

  const { user } = useAuth();
  const { createNotification } = useAdminNotifications();
  const { fetchCategories } = useNewsCategories();
  const { fetchTags } = useNewsTags();
  const { fetchImagesByArticleId } = useNewsImages();

  // Adicionar ref para a seção
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadData();
  }, []);

  // Adicionar um novo useEffect para recarregar os dados quando a seção for ativada
  useEffect(() => {
    if (activeSection === 'news') {
      loadData();
    }
  }, [activeSection]);

  const loadData = async () => {
    try {
      // First, clear any existing error states
      setShowToast(null);

      // Load all data in parallel
      const [articlesData, categoriesData, tagsData] = await Promise.all([
        fetchArticles().catch(error => {
          console.error('Error fetching articles:', error);
          throw new Error('Failed to load articles');
        }),
        fetchCategories().catch(error => {
          console.error('Error fetching categories:', error);
          throw new Error('Failed to load categories');
        }),
        fetchTags().catch(error => {
          console.error('Error fetching tags:', error);
          throw new Error('Failed to load tags');
        })
      ]);

      // Only proceed with image loading if we have articles
      if (articlesData && articlesData.length > 0) {
        const articlesWithImages = await Promise.all(
          articlesData.map(async (article) => {
            try {
              const images = await fetchImagesByArticleId(article.id);
              return { ...article, images };
            } catch (error) {
              console.error(`Error loading images for article ${article.id}:`, error);
              return { ...article, images: [] };
            }
          })
        );
        setArticles(articlesWithImages);
      } else {
        setArticles([]);
      }

      setCategories(categoriesData || []);
      setTags(tagsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setShowToast({
        message: error instanceof Error ? error.message : 'Erro ao carregar dados',
        type: 'error'
      });
      // Set empty states to prevent undefined errors
      setArticles([]);
      setCategories([]);
      setTags([]);
    }
  };

  const handleCreateArticle = () => {
    setEditingArticle({
      title: '',
      excerpt: '',
      content: '',
      status: 'draft',
      is_featured: false,
      is_breaking: false,
      views_count: 0
    });
    setSelectedTags([]);
  };

  const handleEditArticle = async (article: NewsArticleWithRelations) => {
    setEditingArticle(article);
    setSelectedTags(article.tags || []);
  };

  const handleSaveArticle = async () => {
    if (!editingArticle || !user) return;

    try {
      let savedArticle: NewsArticleWithRelations;
      
      const { images, media, category, author, ...articleData } = editingArticle;
      
      if (editingArticle.id) {
        savedArticle = await updateArticle(editingArticle.id, {
          ...articleData,
          tags: selectedTags
        });

        await createNotification({
          title: 'Notícia Atualizada',
          message: `A notícia "${savedArticle.title}" foi atualizada com sucesso.`,
          type: 'success',
          user_id: user.id,
          link: `/news/${savedArticle.slug}`
        });
      } else {
        savedArticle = await createArticle({
          ...articleData,
          tags: selectedTags
        });

        await createNotification({
          title: 'Nova Notícia',
          message: `A notícia "${savedArticle.title}" foi criada com sucesso.`,
          type: 'success',
          user_id: user.id,
          link: `/news/${savedArticle.slug}`
        });
      }

      // Limpar estados
      setEditingArticle(null);
      setSelectedTags([]);
      setShowToast({
        message: 'Notícia salva com sucesso!',
        type: 'success'
      });

      setTimeout(async () => {
        await loadData();
        setShowToast(null);
        // Scroll to the correct position
        const newsSection = document.querySelector('.news-section');
        if (newsSection) {
          const offset = 100;
          const topPosition = newsSection.getBoundingClientRect().top + window.pageYOffset - offset;
          window.scrollTo({
            top: topPosition,
            behavior: 'smooth'
          });
        }
      }, 2000);

    } catch (error) {
      console.error('Error saving article:', error);
      
      setShowToast({
        message: 'Erro ao salvar notícia',
        type: 'error'
      });

      await createNotification({
        title: 'Erro ao Salvar Notícia',
        message: 'Ocorreu um erro ao tentar salvar a notícia. Por favor, tente novamente.',
        type: 'error',
        user_id: user.id
      });
    }
  };

  const handleDeleteArticle = async (id: string) => {
    if (!user) return;
    
    if (window.confirm('Tem certeza que deseja excluir esta notícia?')) {
      try {
        const articleToDelete = articles.find(a => a.id === id);
        await deleteArticle(id);
        await loadData();
        
        setShowToast({
          message: 'Notícia excluída com sucesso!',
          type: 'success'
        });

        // Criar notificação de exclusão
        await createNotification({
          title: 'Notícia Excluída',
          message: `A notícia "${articleToDelete?.title}" foi excluída com sucesso.`,
          type: 'warning',
          user_id: user.id
        });
      } catch (error) {
        console.error('Error deleting article:', error);
        setShowToast({
          message: 'Erro ao excluir notícia',
          type: 'error'
        });

        // Criar notificação de erro na exclusão
        await createNotification({
          title: 'Erro ao Excluir Notícia',
          message: 'Ocorreu um erro ao tentar excluir a notícia. Por favor, tente novamente.',
          type: 'error',
          user_id: user.id
        });
      }
    }
  };

  // Adicionar função para calcular o total de páginas
  const totalPages = Math.ceil(articles.length / articlesPerPage);

  // Calcular o índice inicial e final das notícias para a página atual
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = articles.slice(indexOfFirstArticle, indexOfLastArticle);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    scrollToSection('news-section');
  };

  const handleCloseModal = () => {
    setEditingArticle(null);
    setSelectedTags([]);
  };

  const handleArticleChange = (updatedArticle: NewsArticleWithRelations) => {
    setEditingArticle(updatedArticle);
  };

  const handleImageBrowse = () => {
    console.log('Opening image browser');
    setShowImageBrowser(true);
  };

  const handleImageSelect = (url: string) => {
    console.log('Selected image URL:', url);
    if (editingArticle) {
      setEditingArticle({
        ...editingArticle,
        cover_image: url
      });
    }
    setShowImageBrowser(false);
  };

  if (articlesLoading) {
    return (
      <motion.div 
        className="flex items-center justify-center h-64"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="news-section min-h-[500px] relative"
    >
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-2xl font-bold">Gestão de Notícias</h2>
        <div className="hidden md:block">
          <motion.button
            onClick={handleCreateArticle}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg flex items-center gap-2 hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nova Notícia
          </motion.button>
        </div>
      </div>

      <motion.div 
        className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-lg p-6 border-2 border-dashed border-primary/30 hover:border-primary/50 transition-colors cursor-pointer flex flex-col items-center justify-center gap-4 text-center"
          onClick={handleCreateArticle}
        >
          <Plus className="w-12 h-12 text-primary" />
          <p className="text-lg font-semibold">Adicionar Nova Notícia</p>
          <p className="text-sm text-gray-400">Clique para criar</p>
        </motion.div>

        {currentArticles.map((article, index) => (
          <motion.div
            key={article.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -12, transition: { duration: 0.2 } }}
            className="relative bg-card/20 backdrop-blur-[2px] rounded-2xl p-6 shadow-xl border border-white/5 overflow-hidden group h-full flex flex-col hover:shadow-2xl hover:shadow-primary/20 hover:border-primary/50 transition-all duration-300"
          >
            <div className="relative h-40 shrink-0 mb-4 rounded-xl overflow-hidden">
              <img
                src={article.cover_image || '/placeholder-news.jpg'}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>

            <div className="flex flex-col flex-grow">
              <div className="flex justify-between items-start">
                <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
                  {article.category?.name || 'Sem categoria'}
                </span>
              </div>

              <h3 className="text-xl font-semibold mt-3 mb-2">{article.title}</h3>
              <p className="text-sm text-gray-400 mb-4 line-clamp-2">{article.excerpt}</p>

              <div className="flex items-center justify-between text-sm text-gray-400 mb-3">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(article.published_at || '').toLocaleDateString('pt-PT')}
                </span>
                <span className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  {article.views_count || 0} visualizações
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {article.tags?.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-2 py-1 bg-secondary/50 rounded-full text-xs"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-auto pt-4">
              <div className="h-px bg-white/10 mb-4" />
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => handleEditArticle(article)}
                  className="flex items-center gap-2 px-6 py-2.5 bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                  Editar
                </button>
                <button
                  onClick={() => handleDeleteArticle(article.id)}
                  className="flex items-center gap-2 px-6 py-2.5 bg-destructive/10 hover:bg-destructive/20 text-destructive rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Apagar
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* Modal de Edição/Criação */}
      <AnimatePresence>
        {editingArticle && (
          <NewsArticleForm
            article={editingArticle as NewsArticleWithRelations}
            onSave={handleSaveArticle}
            onClose={handleCloseModal}
            onChange={handleArticleChange}
            onImageBrowse={handleImageBrowse}
            isOpen={!!editingArticle}
            categories={categories}
            tags={tags}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
          />
        )}

        {/* Image Browser Modal */}
        {showImageBrowser && (
          <ImageBrowser
            onSelect={handleImageSelect}
            onClose={() => setShowImageBrowser(false)}
            isAdmin={true}
          />
        )}
      </AnimatePresence>

      {/* Toast de Notificação */}
      {showToast && (
        <Toast
          message={showToast.message}
          type={showToast.type}
          onClose={() => setShowToast(null)} 
          duration={2000}
        />
      )}
      
      {/* Fixed Add Button for Mobile */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={handleCreateArticle}
        className="md:hidden fixed bottom-24 right-4 z-[999] flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full shadow-xl hover:bg-primary/90 transition-colors"
        style={{
          position: 'fixed',
          bottom: '6rem',
          right: '1rem',
          transform: 'translateZ(999px)'
        }}
      >
        <Plus className="w-5 h-5" />
        Nova Notícia
      </motion.button>
    </motion.div>
  );
};